import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axiosInstance from '../lib/axiosInstance';

function ProductDetail() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchProductDetails = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axiosInstance.get(`/brand/details/${id}`);
            if (data.success) {
                setProduct(data.data);
            } else {
                setError('Product not found');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch product details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchProductDetails();
        }
    }, [id, fetchProductDetails]);

    const formatPrice = (price) => {
        return `₹${price?.toLocaleString('en-IN') || '0'}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'in stock': return 'bg-green-100 text-green-800';
            case 'out of stock': return 'bg-red-100 text-red-800';
            case 'draft': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
                <div className="text-[var(--text)]">Loading product details...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
                <div className="text-center">
                    <div className="text-red-600 mb-4">{error}</div>
                    <Link to="/products" className="text-blue-600 hover:text-blue-800">
                        ← Back to Products
                    </Link>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
                <div className="text-center">
                    <div className="text-[var(--text)] mb-4">Product not found</div>
                    <Link to="/products" className="text-blue-600 hover:text-blue-800">
                        ← Back to Products
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--bg)] p-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Link to="/products" className="text-blue-600 hover:text-blue-800">
                            ← Back to Products
                        </Link>
                        <h1 className="lux-heading text-2xl">{product.name}</h1>
                    </div>
                    <Link
                        to={`/products/edit/${product.productID}`}
                        className="px-4 py-2 bg-[var(--accent)] text-[var(--accent-contrast)] rounded-lg hover:opacity-90"
                    >
                        Edit Product
                    </Link>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Product Images */}
                    <div className="lg:col-span-2">
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Product Images</h2>

                            {/* Featured Images */}
                            {product.featuredImage && product.featuredImage.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-md font-medium mb-3">Featured Images</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        {product.featuredImage.map((img, index) => (
                                            <img
                                                key={index}
                                                src={img.imgUrl || '/placeholder-product.jpg'}
                                                alt={img.imgAlt || `Featured image ${index + 1}`}
                                                className="w-full h-48 object-cover rounded-lg"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Gallery Images */}
                            {product.galleryImage && product.galleryImage.length > 0 && (
                                <div>
                                    <h3 className="text-md font-medium mb-3">Gallery Images</h3>
                                    <div className="grid grid-cols-3 gap-4">
                                        {product.galleryImage.map((img, index) => (
                                            <img
                                                key={index}
                                                src={img.imgUrl || '/placeholder-product.jpg'}
                                                alt={img.imgAlt || `Gallery image ${index + 1}`}
                                                className="w-full h-32 object-cover rounded-lg"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Product Information */}
                    <div className="space-y-6">
                        {/* Basic Info */}
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Product Information</h2>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm text-[var(--muted)]">Product ID</label>
                                    <div className="font-mono text-sm">{product.productID}</div>
                                </div>
                                <div>
                                    <label className="text-sm text-[var(--muted)]">Name</label>
                                    <div className="font-medium">{product.name}</div>
                                </div>
                                <div>
                                    <label className="text-sm text-[var(--muted)]">Status</label>
                                    <div>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(product.status)}`}>
                                            {product.status}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm text-[var(--muted)]">Type</label>
                                    <div className="bg-[var(--surface)] px-2 py-1 rounded text-sm inline-block">
                                        {product.type}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm text-[var(--muted)]">Brand</label>
                                    <div>{product.brand}</div>
                                </div>
                                <div>
                                    <label className="text-sm text-[var(--muted)]">Created</label>
                                    <div className="text-sm">{formatDate(product.createdAt)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Pricing */}
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Pricing</h2>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-[var(--muted)]">Regular Price:</span>
                                    <span className="font-medium">{formatPrice(product.regularPrice)}</span>
                                </div>
                                {product.salePrice && product.salePrice !== product.regularPrice && (
                                    <div className="flex justify-between">
                                        <span className="text-[var(--muted)]">Sale Price:</span>
                                        <span className="font-medium text-green-600">{formatPrice(product.salePrice)}</span>
                                    </div>
                                )}
                                {product.discountValue > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-[var(--muted)]">Discount:</span>
                                        <span className="text-red-600">
                                            {product.discountType === 'percentage' ? `${product.discountValue}%` : formatPrice(product.discountValue)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Categories */}
                        {product.categories && product.categories.length > 0 && (
                            <div className="card p-6">
                                <h2 className="text-lg font-semibold mb-4">Categories</h2>
                                <div className="flex flex-wrap gap-2">
                                    {product.categories.map((category, index) => (
                                        <span
                                            key={index}
                                            className="bg-[var(--surface)] px-2 py-1 rounded text-sm"
                                        >
                                            {category?.categoryName || category?.name || String(category)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Description and Tabs */}
                <div className="mt-6 space-y-6">
                    {/* Description */}
                    {product.description && (
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Description</h2>
                            <div className="prose max-w-none text-[var(--text)]">
                                {product.description}
                            </div>
                        </div>
                    )}

                    {/* Tab Content */}
                    {(product.tab1 || product.tab2 || product.tab3) && (
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Additional Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {product.tab1 && (
                                    <div>
                                        <h3 className="font-medium mb-2">Tab 1</h3>
                                        <div className="prose max-w-none text-[var(--text)]">
                                            {product.tab1}
                                        </div>
                                    </div>
                                )}
                                {product.tab2 && (
                                    <div>
                                        <h3 className="font-medium mb-2">Tab 2</h3>
                                        <div className="prose max-w-none text-[var(--text)]">
                                            {product.tab2}
                                        </div>
                                    </div>
                                )}
                                {product.tab3 && (
                                    <div>
                                        <h3 className="font-medium mb-2">Tab 3</h3>
                                        <div className="prose max-w-none text-[var(--text)]">
                                            {product.tab3}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Attributes */}
                    {product.productAttributes && product.productAttributes.length > 0 && (
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Product Attributes</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {product.productAttributes.map((attr, index) => (
                                    <div key={index} className="flex justify-between">
                                        <span className="text-[var(--muted)]">{attr.name}:</span>
                                        <span>{attr.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Variations */}
                    {product.variations && product.variations.length > 0 && (
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Variations</h2>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="border-b border-[var(--border)]">
                                            <th className="text-left py-2 px-3 text-xs text-[var(--muted)]">Variation</th>
                                            <th className="text-right py-2 px-3 text-xs text-[var(--muted)]">Price</th>
                                            <th className="text-right py-2 px-3 text-xs text-[var(--muted)]">Sale Price</th>
                                            <th className="text-right py-2 px-3 text-xs text-[var(--muted)]">Stock</th>
                                            <th className="text-left py-2 px-3 text-xs text-[var(--muted)]">Values</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {product.variations.map((v, idx) => (
                                            <tr key={idx} className="border-b border-[var(--border)]">
                                                <td className="py-2 px-3">{v.variationName || '-'}</td>
                                                <td className="py-2 px-3 text-right">{formatPrice(Number(v.variationPrice || 0))}</td>
                                                <td className="py-2 px-3 text-right">{v.variationSalePrice ? formatPrice(Number(v.variationSalePrice)) : '-'}</td>
                                                <td className="py-2 px-3 text-right">{v.variationStock ?? '-'}</td>
                                                <td className="py-2 px-3">
                                                    {(Array.isArray(v.variationValues) ? v.variationValues : []).join(', ')}
                                                </td>
                                            </tr>))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProductDetail;
